/**
 * @fileoverview AR-924 — Dev-only decoration of the real matrix with mock teacher uploads, so every
 * deliverable-cell state is reviewable without a backend. Writes to the mock store: never run during render.
 */

import { getMockDelivery, seedMockDelivery } from './adapters/mockStore'
import type { StudentActivityStatus, TaskTrackCellDelivery, TaskTrackMatrix, TaskTrackDelivery } from '../types'

const OTHER_TEACHER = { id: 'mock-teacher-ana', name: 'Ana Passos', currentUser: false }
const thumb = (fill: string) =>
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="4" height="3">' +
  `<rect width="4" height="3" fill="%23${fill}"/></svg>`
const BEIGE_THUMB = thumb('F6F1E4')
const BLUE_THUMB = thumb('EEF1FA')
const DAY_MS = 24 * 60 * 60 * 1000
const MOCK_FILE_BYTES = 2_202_009

function isoDay(offsetDays: number, from = new Date()): string {
  return new Date(from.getTime() + offsetDays * DAY_MS).toISOString().slice(0, 10)
}

const mockPhoto = (id: string, name: string, fileCover: string) =>
  ({ id, name, fileType: 'image', downloadUrl: '#', sizeInBytes: MOCK_FILE_BYTES, fileCover })

const mockLink = (addedBy: TaskTrackDelivery['uploadedBy']) => ({
  id: 'ml-1',
  url: 'https://docs.google.com/document/d/andrews-shapes',
  createdAt: new Date().toISOString(),
  addedBy,
})

/** Row 0 ("you") completes the work on the lesson day → on time; row 1 (Ana) completes it today → late. */
function teacherDelivery(byYou: boolean, lessonDate: string): TaskTrackDelivery {
  const uploadedBy = byYou ? { id: 'mock-teacher-you', name: 'You', currentUser: true } : OTHER_TEACHER
  const lessonDay = lessonDate ? new Date(`${lessonDate}T12:00:00`) : new Date()
  return {
    id: byYou ? 'mock-delivery-you' : 'mock-delivery-other',
    status: 'done',
    description: '',
    note: '',
    submittedAt: new Date().toISOString(),
    late: !byYou,
    workCompletedAt: byYou ? isoDay(0, lessonDay) : isoDay(0),
    uploadedBy,
    files: [mockPhoto('mf-1', 'IMG_2041.jpg', BEIGE_THUMB), mockPhoto('mf-2', 'IMG_2042.jpg', BLUE_THUMB)],
    links: byYou ? [mockLink(uploadedBy)] : [],
    source: 'platform',
  }
}

function cellOf(delivery: TaskTrackDelivery): TaskTrackCellDelivery {
  return {
    id: delivery.id,
    uploadedByTeacher: true,
    uploadedById: delivery.uploadedBy?.id ?? null,
    uploadedByName: delivery.uploadedBy?.name ?? null,
    uploadedByCurrentUser: Boolean(delivery.uploadedBy?.currentUser),
    submittedAt: delivery.submittedAt,
    workCompletedAt: delivery.workCompletedAt ?? null,
    filesCount: delivery.files.length,
    linksCount: (delivery.links ?? []).length,
  }
}

/** Cells seeded once per session, so edits made through the drawer survive a matrix refresh. */
const seededKeys = new Set<string>()
const keyOf = (activityId: string, studentId: string) => `${activityId}:${studentId}`

function cellFromStore(cell: StudentActivityStatus, studentId: string): StudentActivityStatus {
  const delivery = getMockDelivery(cell.activityId, studentId)
  if (!delivery) return { ...cell, deliverableCompleted: false, deliverable: null }
  return { ...cell, deliverableCompleted: true, deliverable: cellOf(delivery) }
}

export function decorateMatrixWithMockUploads(matrix: TaskTrackMatrix): TaskTrackMatrix {
  const first = matrix.activities.find(activity => activity.hasDeliverables)
  if (!first) return matrix

  matrix.students.slice(0, 2).forEach((student, row) => {
    const key = keyOf(first.id, student.id)
    if (seededKeys.has(key)) return
    seedMockDelivery(first.id, student.id, teacherDelivery(row === 0, first.lessonDate))
    seededKeys.add(key)
  })

  const isMocked = (cell: StudentActivityStatus, studentId: string) =>
    seededKeys.has(keyOf(cell.activityId, studentId)) || Boolean(getMockDelivery(cell.activityId, studentId))

  const students = matrix.students.map(student => ({
    ...student,
    activities: student.activities.map(cell => (isMocked(cell, student.id) ? cellFromStore(cell, student.id) : cell)),
  }))
  return { ...matrix, students }
}
